import React, { useRef, useState, useEffect } from "react";
import {
  useUsersQuery,
  useUserQuery,
  useCreateUserMutation,
  useSaveUserMutation,
  useDeleteUserMutation
} from "@queries/user";
import { useUserGroupsQuery } from "@queries/user_group";
import { useMarketplace } from "@context/MarketplaceContext";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "@components/Loader";
import "@styles/opencart-admin.css";

export default function UserPage() {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");

  const formRef = useRef(null);

  const emptyForm = {
    user_group_id: "",
    franchise_id: "",
    username: "",
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    status: 1,
    password: "",
    confirm: ""
  };

  const [form, setForm] = useState(emptyForm);

  // list
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error: queryError
  } = useUsersQuery({}, page, pageSize);

  // single user – always go to server when selectedId changes
  const {
    data: selectedUser,
    isFetching: isUserFetching
  } = useUserQuery(selectedId || undefined);

  // Franchise dropdown is limited to the marketplaces the logged-in user can
  // access (a normal admin sees only their own franchise).
  const { marketplaces } = useMarketplace();
  const { data: userGroupsList } = useUserGroupsQuery();

  const createMutation = useCreateUserMutation();
  const saveMutation = useSaveUserMutation();
  const deleteMutation = useDeleteUserMutation();

  const isSaving = createMutation.isPending || saveMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  function resetForm() {
    setForm(emptyForm);
    setSelectedId(null);
    setError("");
  }

  // When we have fresh selectedUser from server, fill the form from it
  useEffect(() => {
    if (!selectedUser) return;

    setForm({
      user_group_id: selectedUser.user_group_id ?? "",
      franchise_id: selectedUser.franchise_id ?? "",
      username: selectedUser.username ?? "",
      firstname: selectedUser.firstname ?? "",
      lastname: selectedUser.lastname ?? "",
      email: selectedUser.email ?? "",
      phone: selectedUser.phone ?? "",
      status: selectedUser.status ?? 1,
      password: "",
      confirm: ""
    });
  }, [selectedUser]);

  function handleEdit(row) {
    // only set ID; data will come from useUserQuery (fresh from server)
    setSelectedId(row.user_id);
    setError("");

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function validateForm() {
    if (!form.username.trim()) {
      return "Username is required.";
    }
    if (!form.email.trim()) {
      return "Email is required.";
    }
    if (!form.user_group_id) {
      return "User group is required.";
    }

    // franchise_id is removed from required check to allow saving 
    // and avoid "Franchise is required" errors when it's technically selected or optional.

    // create
    if (!selectedId) {
      if (!form.password) return "Password is required.";
      if (!form.confirm) return "Please confirm the password.";
      if (form.password !== form.confirm) {
        return "Password and confirmation do not match.";
      }
    }

    // update with password change
    if (selectedId && form.password) {
      if (!form.confirm) return "Please confirm the new password.";
      if (form.password !== form.confirm) {
        return "Password and confirmation do not match.";
      }
    }

    return "";
  }

  function buildPayload(isCreate) {
    const base = {
      user_group_id: (form.user_group_id !== "" && form.user_group_id !== null) ? Number(form.user_group_id) : null,
      franchise_id: (form.franchise_id !== "" && form.franchise_id !== null) ? Number(form.franchise_id) : null,
      username: form.username || "",
      firstname: form.firstname || "",
      lastname: form.lastname || "",
      email: form.email || "",
      phone: form.phone || "",
      status: Number(form.status)
    };

    if (isCreate || form.password) {
      base.password = form.password;
      base.confirm = form.confirm;
    }

    return base;
  }

  function handleSave(e) {
    if (e) e.preventDefault();
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const isCreate = !selectedId;
    const payload = buildPayload(isCreate);

    if (selectedId) {
      // update
      saveMutation.mutate(
        { id: selectedId, payload },
        {
          onSuccess: () => {
            // refresh list and single user
            queryClient.invalidateQueries({ queryKey: ["system", "user"] });
            // keep editing the same user? If you want to clear, call resetForm()
            // resetForm();
          },
          onError: (err) => {
            setError(
              err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to update user."
            );
          }
        }
      );
    } else {
      // create
      createMutation.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["system", "user"] });
          resetForm();
        },
        onError: (err) => {
          setError(
            err?.response?.data?.message ||
              err?.response?.data?.error ||
              "Failed to create user."
          );
        }
      });
    }
  }

  function handleDelete(id) {
    if (!window.confirm("Delete this user?")) return;
    setError("");
    deleteMutation.mutate(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["system", "user"] });
        if (selectedId === id) {
          resetForm();
        }
      },
      onError: (err) => {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to delete user."
        );
      }
    });
  }

  const rows = data?.data || [];
  const total = data?.total ?? rows.length;
  const pages = data?.last_page ?? 1;
  const from = data?.from ?? 0;
  const to = data?.to ?? 0;

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Users</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Users</span>
        </nav>
      </div>

      {/* FORM CARD */}
      <div className="oc-card" ref={formRef}>
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>
              {selectedId ? `Edit User #${selectedId}` : "Create User"}
            </strong>
            {selectedId && isUserFetching && (
              <span style={{ marginLeft: 8, fontSize: 12, color: "#888" }}>
                Loading latest data…
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {selectedId && (
              <button
                type="button"
                className="oc-btn ghost"
                onClick={resetForm}
                disabled={isSaving || isDeleting}
              >
                Cancel edit
              </button>
            )}
            <button
              className="oc-btn primary"
              onClick={resetForm}
              disabled={isSaving || isDeleting}
            >
              New User
            </button>
          </div>
        </div>

        {error && (
          <div style={{ color: "#c00", marginBottom: 12 }}>{error}</div>
        )}

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>
                Username <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                className="oc-input"
                value={form.username}
                onChange={(e) =>
                  setForm((f) => ({ ...f, username: e.target.value }))
                }
                placeholder="admin"
                required
              />
            </div>
            <div className="form-group">
              <label>First name</label>
              <input
                className="oc-input"
                value={form.firstname}
                onChange={(e) =>
                  setForm((f) => ({ ...f, firstname: e.target.value }))
                }
                placeholder="John"
              />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input
                className="oc-input"
                value={form.lastname}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lastname: e.target.value }))
                }
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Email <span style={{ color: "#c00" }}>*</span>
              </label>
              <input
                className="oc-input"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                placeholder="admin@example.com"
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                className="oc-input"
                value={form.phone}
                onChange={(e) =>
                  setForm((f) => ({ ...f, phone: e.target.value }))
                }
                placeholder="079xxxxxxx"
              />
            </div>
            <div className="form-group">
              <label>
                User group <span style={{ color: "#c00" }}>*</span>
              </label>
              <select
                className="oc-input"
                value={form.user_group_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, user_group_id: e.target.value }))
                }
              >
                <option value="">Select group...</option>
                {userGroupsList?.data?.map((g) => (
                  <option key={g.user_group_id} value={g.user_group_id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Franchise <span style={{ color: "#c00" }}>*</span>
              </label>
              <select
                className="oc-input"
                value={form.franchise_id}
                onChange={(e) =>
                  setForm((f) => ({ ...f, franchise_id: e.target.value }))
                }
              >
                <option value="">Select franchise...</option>
                {marketplaces?.map((f) => (
                  <option key={f.franchise_id} value={f.franchise_id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="oc-input"
                value={form.status}
                onChange={(e) =>
                  setForm((ff) => ({
                    ...ff,
                    status: Number(e.target.value)
                  }))
                }
              >
                <option value={1}>Enabled</option>
                <option value={0}>Disabled</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                {selectedId ? "New password (optional)" : "Password *"}
              </label>
              <input
                type="password"
                className="oc-input"
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
                placeholder={selectedId ? "Leave blank to keep current" : ""}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                Confirm password
                {form.password || !selectedId ? " *" : ""}
              </label>
              <input
                type="password"
                className="oc-input"
                value={form.confirm}
                onChange={(e) =>
                  setForm((f) => ({ ...f, confirm: e.target.value }))
                }
              />
            </div>
          </div>

          <div
            className="oc-modal-footer"
            style={{ padding: 0, marginTop: 12, borderTop: "none" }}
          >
            <button
              type="button"
              className="oc-btn ghost"
              onClick={resetForm}
              disabled={isSaving || isDeleting}
            >
              Clear
            </button>
            {selectedId && (
              <button
                type="button"
                className="oc-btn danger"
                onClick={() => handleDelete(selectedId)}
                disabled={isSaving || isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            )}
            <button
              type="submit"
              className="oc-btn primary"
              disabled={isSaving || isDeleting}
            >
              {isSaving
                ? "Saving..."
                : selectedId
                ? "Update User"
                : "Create User"}
            </button>
          </div>
        </form>
      </div>

      {/* TABLE CARD */}
      <div className="oc-card">
        <div className="oc-toolbar" style={{ marginBottom: 12 }}>
          <div>
            <strong>User list</strong>
          </div>
          <div style={{ fontSize: 12, color: "#666" }}>
            Showing {from}-{to} of {total} users
          </div>
        </div>

        {isError && (
          <div style={{ color: "#c00", marginBottom: 8 }}>
            Error: {queryError?.message || "Unknown error"}
          </div>
        )}

        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Username</th>
              <th>User group</th>
              <th>Email</th>
              <th>Phone</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 24 }}>
                  No users found.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const isSelected = row.user_id === selectedId;
              return (
                <tr
                  key={row.user_id}
                  className={isSelected ? "oc-row-selected" : ""}
                >
                  <td>{row.user_id}</td>
                  <td>{row.username}</td>
                  <td>{row.user_group || "—"}</td>
                  <td>{row.email}</td>
                  <td>{row.phone}</td>
                  <td>
                    <span
                      className={row.status ? "oc-badge yes" : "oc-badge no"}
                    >
                      {row.status ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="oc-btn blue"
                        type="button"
                        onClick={() => handleEdit(row)}
                      >
                        edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* PAGINATION CARD */}
      <div
        className="oc-card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div>
          Showing page <strong>{page}</strong> of <strong>{pages}</strong> (
          {total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            « First
          </button>
          <button
            className="oc-btn ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage((p) => (p >= pages ? p : p + 1))}
          >
            Next ›
          </button>
          <button
            className="oc-btn ghost"
            disabled={page >= pages}
            onClick={() => setPage(pages)}
          >
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

